<template>
  <div class="compliance overflow-hidden rounded-md border border-solid border-[--color-neutral-a4]">
    <div
      class="font-medium leading-6 [&_>div:first-child]:border-0 [&_>div:last-child]:border-0 [&_>div]:flex [&_>div]:justify-between [&_>div]:gap-4 [&_>div]:border-b [&_>div]:border-[--color-neutral-a4] [&_>div]:px-5 [&_>div]:py-3 [&_>div]:font-bold"
    >
      <div v-if="!displayDownloadButton" class="bg-[--color-neutral-a6] !py-[14px]">
        <h3 class="m-0 text-lg font-semibold text-[--color-neutral-a05] lg:text-xl">
          {{ $t("pages.product.documents.compliance_info") }}
        </h3>
        <button
          v-if="showDownloadAllButton"
          type="button"
          class="flex items-center gap-1 whitespace-nowrap font-medium text-[--color-link] hover:text-[--color-neutral-a1] disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isDownloadingCertificates"
          @click="downloadAllCertificates"
        >
          <VcIcon name="download" :size="14" class="mt-1 self-center" />
          <span class="hide-xxs">{{ $t("pages.product.documents.download_all") }}</span>
        </button>
      </div>
      <div v-if="rohsCertificatesInfo.hasCertificate" class="compliance__item flex items-center">
        <div class="flex flex-col gap-2">
          <span>RoHS</span>
          <div v-for="value in rohsCertificatesInfo.values" :key="value" class="text-sm font-normal">
            <span v-dompurify-html="value" class="text-sm font-normal" />
          </div>
        </div>
        <button
          v-if="rohsCertificatesInfo.shouldGenerateCertificate"
          type="button"
          class="font-medium text-[--color-link] hover:text-[--color-neutral-a1]"
          @click="openRohsCertificate"
        >
          {{ $t("pages.product.documents.certificate") }}
        </button>
      </div>
      <div v-if="reachCertificatesInfo.hasCertificate" class="compliance__item flex items-center">
        <div class="flex flex-col gap-2">
          <span>REACH</span>
          <span v-dompurify-html="reachCerficateformattedValues" class="flex flex-col gap-2 text-sm font-normal"></span>
        </div>
        <button
          v-if="reachCertificatesInfo.shouldGenerateCertificate"
          type="button"
          class="font-medium text-[--color-link] hover:text-[--color-neutral-a1]"
          @click="openReachCertificate"
        >
          {{ $t("pages.product.documents.certificate") }}
        </button>
      </div>
      <div v-if="tscaCertificatesInfo.hasCertificate" class="compliance__item flex items-center">
        <div class="flex flex-col gap-2">
          <span>TSCA</span>
          <div v-for="value in tscaCertificatesInfo.values" :key="value" class="text-sm font-normal">
            <span v-dompurify-html="value" class="text-sm font-normal" />
          </div>
        </div>
        <button
          v-if="tscaCertificatesInfo.shouldGenerateCertificate"
          type="button"
          class="font-medium text-[--color-link] hover:text-[--color-neutral-a1]"
          @click="openTscaCertificate"
        >
          {{ $t("pages.product.documents.certificate") }}
        </button>
      </div>
      <div v-if="!hideComplianceEmail" class="compliance__email flex items-center !py-[14px]">
        <div class="text-13 font-medium">
          {{ $t("pages.product.documents.compliance_questions") }}
          <span class="inline-block"
            >{{ $t("pages.company.members.content_header.email") }}
            <a :href="`mailto:${complianceEmail}`" class="text-[--color-link] hover:text-[--color-neutral-a1]">
              {{ complianceEmail }} </a
            >.
          </span>
        </div>
      </div>
    </div>
    <ReachDialog
      v-if="isReachDialogMounted"
      ref="reachRef"
      :product-number="product.code"
      :product-description="productTitleDescription"
      :product-id="product.id"
      :certificate-info="reachCertificatesInfo"
    />
    <RohsDialog
      v-if="isRohsDialogMounted"
      ref="rohsRef"
      :product-number="product.code"
      :product-description="productTitleDescription"
      :product-id="product.id"
      :certificate-info="rohsCertificatesInfo"
    />
    <TscaDialog
      v-if="isTscaDialogMounted"
      ref="tscaRef"
      :product-number="product.code"
      :product-description="productTitleDescription"
      :product-id="product.id"
      :certificate-info="tscaCertificatesInfo"
    />
  </div>
  <VcButton
    v-if="displayDownloadButton"
    class="w-full"
    variant="outlineTL"
    color="secondary"
    size="lg"
    :disabled="isDownloadingCertificates"
    @click="downloadAllCertificates"
  >
    <VcIcon name="download" :size="20" class="mr-2.5 mt-1" />
    {{ $t("pages.product.documents.download_all") }}
  </VcButton>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { useCurrency } from "@/core/composables";
import { useGeolocation } from "@/core/composables/useGeolocation";
import { SPECIAL_CASES_EUR } from "@/core/constants/language-and-currency";
import { globals } from "@/core/globals";
import { ReachDialog, RohsDialog, TscaDialog } from "..";
import {
  downloadFile,
  getCertificationInfo,
  REACH_CERTIFICATE_TYPE,
  reachDoNotGenerate,
  ROHS_CERTIFICATE_TYPE,
  rohsDoNotGenerate,
  TSCA_CERTIFICATE_TYPE,
  tscaDoNotGenerate,
} from "../types";
import type { ICertificateInfo } from "../types";
import type { Product } from "@/core/api/graphql/types";

interface IProps {
  product: Product;
  displayDownloadButton: boolean;
  hideComplianceEmail?: boolean | undefined;
}

const props = defineProps<IProps>();
const { currentCurrency } = useCurrency();
const { countryCodeISO3 } = useGeolocation();

// Match the print page: use the Title-type description, falling back to the product name
const productTitleDescription = computed(
  () =>
    props.product.descriptions?.find((description) => description?.reviewType === "Title")?.content ??
    props.product.fallbackName,
);

// Resolve EUR special cases (FRA/SWE) so the backend Puppeteer context gets the correct region
const resolvedCurrencyCode = computed(() => {
  const code = currentCurrency.value.code;
  if (code === "EUR") {
    if (SPECIAL_CASES_EUR.FRA.includes(countryCodeISO3.value)) {
      return "FRA";
    }
    if (SPECIAL_CASES_EUR.SWE.includes(countryCodeISO3.value)) {
      return "SWE";
    }
  }
  return code;
});

const rohsCertificatesInfo = computed<ICertificateInfo>(() => {
  const rohsCertificatesInfoData = getCertificationInfo(
    props.product.compliancesTL.filter((certificate) => certificate.categoryName === ROHS_CERTIFICATE_TYPE),
    rohsDoNotGenerate,
  );

  // Hack due to the automatic interpretation of (c) as the copyright symbol © by the browser when rendering the text in HTML
  rohsCertificatesInfoData.values = rohsCertificatesInfoData.values.map((value) => {
    return value?.replace(/\(c\)/g, "<span class='inline-flex'><span>(</span> c <span>)</span></span>");
  });

  return rohsCertificatesInfoData;
});

const reachCertificatesInfo = computed<ICertificateInfo>(() => {
  const reachCertificatesInfoData = getCertificationInfo(
    props.product.compliancesTL.filter((certificate) => certificate.categoryName === REACH_CERTIFICATE_TYPE),
    reachDoNotGenerate,
  );

  // Hack due to the automatic interpretation of (c) as the copyright symbol © by the browser when rendering the text in HTML
  reachCertificatesInfoData.values = reachCertificatesInfoData.values.map((value) => {
    return value?.replace(/\(c\)/g, "<span class='inline-flex'><span>(</span> c <span>)</span></span>");
  });

  return reachCertificatesInfoData;
});

const reachCerficateformattedValues = computed(() => {
  return reachCertificatesInfo.value.values
    .map((value) => {
      return value?.toLocaleLowerCase().includes("nosvhc") || value?.toLocaleLowerCase().includes("svhc free")
        ? "Compliant"
        : value;
    })
    .map((value) => `<div>${value}</div>`)
    .join("");
});

const tscaCertificatesInfo = computed<ICertificateInfo>(() => {
  return getCertificationInfo(
    props.product.compliancesTL.filter((certificate) => certificate.categoryName === TSCA_CERTIFICATE_TYPE),
    tscaDoNotGenerate,
  );
});

const showDownloadAllButton = computed(() => {
  return (
    rohsCertificatesInfo.value.shouldGenerateCertificate ||
    reachCertificatesInfo.value.shouldGenerateCertificate ||
    tscaCertificatesInfo.value.shouldGenerateCertificate
  );
});

const reachRef = ref<InstanceType<typeof ReachDialog> | null>(null);
const rohsRef = ref<InstanceType<typeof RohsDialog> | null>(null);
const tscaRef = ref<InstanceType<typeof TscaDialog> | null>(null);
const isReachDialogMounted = ref(false);
const isRohsDialogMounted = ref(false);
const isTscaDialogMounted = ref(false);
const isDownloadingCertificates = ref(false);

const downloadAllCertificates = async () => {
  if (isDownloadingCertificates.value) {
    return;
  }

  isDownloadingCertificates.value = true;

  try {
    await downloadFile(
      `/api/thorlabs-products/certificates-zip/${props.product.id}/${resolvedCurrencyCode.value}`,
      `${props.product.code}-Compliance.zip`,
      {
        responseType: "arraybuffer",
        timeout: 200000,
      },
      { type: "application/zip" },
    );
  } finally {
    isDownloadingCertificates.value = false;
  }
};

const openReachCertificate = async () => {
  isReachDialogMounted.value = true;
  // Wait for next tick to ensure component is mounted
  await new Promise((resolve) => setTimeout(resolve, 0));
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  reachRef.value?.open();
};

const openRohsCertificate = async () => {
  isRohsDialogMounted.value = true;
  // Wait for next tick to ensure component is mounted
  await new Promise((resolve) => setTimeout(resolve, 0));
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  rohsRef.value?.open();
};

const openTscaCertificate = async () => {
  isTscaDialogMounted.value = true;
  // Wait for next tick to ensure component is mounted
  await new Promise((resolve) => setTimeout(resolve, 0));
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  tscaRef.value?.open();
};

const complianceEmail = globals.currencyCode == "JPY" ? "techsupport.jp@thorlabs.com" : "compliance@thorlabs.com";
</script>
